import type { FieldData as DocumentFieldData } from "./DocumentExport";
import type { FieldData as ExcelFieldData } from "./ExcelExport";
import type { FieldData as ImageFieldData } from "./ImageExport";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { GetOptionKey } from "@/appComponents/Navigation/RightSidebar/PageProperties/PagePropertiesUtils";
import { isNullish } from "@theorgchartdev/storybook";
import { NormalizeSCWiz } from "@/appComponents/Navigation/RightSidebar/Optimize/Optimize.utils";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumDataSource,
  enumPageSizes,
  enumSubChartOption,
  enumObjClassNames
} from "@coreapp/constants";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { QXWSMgr } from "@coreapp/CoreApp/QXWSMgr";

type ReportPartial = {
  columnNames: string[];
  ID: Nullable<string>;
  name: Nullable<string>;
};

export const getDefaultExportName = (report?: ReportPartial): string => {
  if (isInReporting()) {
    return report?.name ?? appstr("Untitled Report");
  }

  const wsMgr: Nullish<QXWSMgr> = GetWSMgr();
  const docMgr = wsMgr?.GetDocMgr();
  if (isNullish(docMgr)) {
    return appstr("Export");
  }

  if (docMgr.GetIWizDataInteractive()?.GetDataSource() === enumDataSource.pointintime) {
    return docMgr.GetIWizDataInteractive().GetArchiveString(wsMgr);
  }

  return docMgr.GetFormattedChartName(false, false);
};

export const hasInvalidFilenameChars = (name: string): boolean => /[\\/:*?"<>|]/.test(name);

export const isAccessibilityReportAvailable = (doc?: Nullable<QXDoc>): boolean => {
  // custom (client-loaded) charts export client-side, and plan charts are excluded
  return doc?.IsServerChart?.() === true && doc?.IsPlanChart?.() !== true;
};

export const getDefaultPageSize = (doc?: Nullable<QXDoc>) => {
  const pageMgr = doc?.GetPageMgr();
  const pageProps = pageMgr?.GetPageProps();

  if (isNullish(pageMgr) || isNullish(pageProps)) {
    return "Letter";
  }

  return GetOptionKey(enumPageSizes, [pageProps.fPageHeight, pageProps.fPageWidth]) ?? "Letter";
};

export const getInitialPageSize = (
  doc: Nullable<QXDoc>,
  existingSettings: DefaultValues["document"]
) => {
  if (existingSettings["scope"] === "Page") {
    return {};
  }

  if (existingSettings["pageSize"] !== "current") {
    return {};
  }

  return {
    pageSize: getDefaultPageSize(doc)
  };
};

export const getInitialPagination = (
  doc: Nullable<QXDoc>,
  existingSettings: DefaultValues["document"]
) => {
  const scWiz = doc?.GetIWizData() ?? {};
  const currentPagination = NormalizeSCWiz(scWiz);

  const boxClass = doc?.GetBoxClass();
  const divisionFieldDef = boxClass?.GetFieldDefByID("Division");
  const divisionFieldName = divisionFieldDef?.GetFieldName() ?? "";

  if (!existingSettings["breakfield"]) {
    existingSettings["breakfield"] = divisionFieldName;
  }

  if (!currentPagination["breakfield"]) {
    currentPagination["breakfield"] = divisionFieldName;
  }

  if (!doc || doc?.IsServerChart()) {
    if (existingSettings["scChartBreakType"] !== enumSubChartOption.keepAsIs) {
      // In this case, we are not overriding the stored settings from localStorage
      return existingSettings;
    }

    // If the stored settings are set to keep as is with a base chart, we should default to auto breaks
    return { ...currentPagination, scChartBreakType: enumSubChartOption.autobreaks };
  }

  // const hasPaginationOverride = doc.GetSubChartMgr().fManualPaginationDeltas.some(item => {
  //   return ["insert", "remove"].includes(item.GetBreakType());
  // });

  // if (hasPaginationOverride) {
  //   return { ...currentPagination, scChartBreakType: enumSubChartOption.keepAsIs };
  // }

  // For all custom charts, we should default to keep pagination as is
  return { ...currentPagination, scChartBreakType: enumSubChartOption.keepAsIs };
};

type ExportSettings = {
  excel?: Omit<Partial<ExcelFieldData>, "name">;
  image?: Omit<Partial<ImageFieldData>, "name">;
  document?: Omit<Partial<DocumentFieldData>, "name">;
};

type MappedSettings = {
  [partitionId: string]: Optional<ExportSettings>;
};

const getPartitionId = (report?: ReportPartial) => {
  const wsMgr: Nullish<QXWSMgr> = GetWSMgr();
  if (isNullish(wsMgr)) {
    return null;
  }

  const userName = wsMgr.GetUserName();

  if (isInReporting()) {
    if (!report?.ID) {
      return null;
    }

    return `${userName}_${report.ID}`;
  }

  const doc: Nullish<QXDoc> = GetDoc();
  if (isNullish(doc)) {
    return null;
  }

  return `${userName}_${doc.GetChartID()}`;
};

type DefaultValues = {
  excel: Omit<ExcelFieldData, "name">;
  image: Omit<ImageFieldData, "name">;
  document: Omit<DocumentFieldData, "name">;
};

export type CategoryFields = {
  position: { included: string[]; excluded: string[] };
  employee: { included: string[]; excluded: string[] };
};

type StoredValue = number | string | string[] | boolean | undefined | CategoryFields;

const readExportSetting = (
  storedSettings: any,
  partitionId: Nullable<string>,
  format: string,
  key: string,
  defaultValue: StoredValue | (() => StoredValue)
) => {
  const storedValue = storedSettings[partitionId ?? ""]?.[format]?.[key];
  if (!isNullish(storedValue)) {
    return storedValue;
  }

  if (typeof defaultValue === "function") {
    return defaultValue();
  }

  return defaultValue;
};

export const storeExportSettings = <F extends keyof ExportSettings>(
  format: F,
  key: string,
  value: StoredValue,
  report?: ReportPartial
) => {
  const storedFields = localStorage.getItem("exportSettings");
  const mappedFields: MappedSettings = JSON.parse(storedFields ?? "{}");
  const partitionId = getPartitionId(report);

  if (isNullish(partitionId)) {
    return;
  }

  mappedFields[partitionId] = {
    ...mappedFields[partitionId],
    [format]: {
      ...mappedFields[partitionId]?.[format],
      [key]: value
    }
  };

  localStorage.setItem("exportSettings", JSON.stringify(mappedFields));
};

const coerceStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

const sanitizeCategoryFields = (
  fields: CategoryFields,
  allowedFieldSet: Set<string>
): CategoryFields => ({
  position: {
    included: coerceStringArray(fields.position?.included).filter(f => allowedFieldSet.has(f)),
    excluded: coerceStringArray(fields.position?.excluded).filter(f => allowedFieldSet.has(f))
  },
  employee: {
    included: coerceStringArray(fields.employee?.included).filter(f => allowedFieldSet.has(f)),
    excluded: coerceStringArray(fields.employee?.excluded).filter(f => allowedFieldSet.has(f))
  }
});

export const readStoredExportSettings = <F extends keyof DefaultValues>(
  defaultValues: DefaultValues[F],
  format: F
): DefaultValues[F] => {
  const exportSettings = localStorage.getItem("exportSettings");
  const storedSettings: MappedSettings = JSON.parse(exportSettings ?? "{}");
  const partitionId = getPartitionId();

  const storedValues = { ...defaultValues };
  const defaultValueKeys = Object.keys(defaultValues) as Array<keyof DefaultValues[F]>;

  for (const key of defaultValueKeys) {
    storedValues[key] = readExportSetting(
      storedSettings,
      partitionId,
      format,
      String(key),
      defaultValues[key] as StoredValue
    );
  }

  return storedValues;
};

function migrateLegacyFieldSelection(
  legacyIncluded: string[],
  legacyExcluded: string[],
  doc: ReturnType<typeof GetDoc>
): CategoryFields {
  if (doc?.IsPositionDocument()) {
    const { keys, employeeKeys } = getEmployeePositionFields(doc);
    const positionKeySet = new Set(keys);
    const employeeKeySet = new Set(employeeKeys);
    return {
      position: {
        included: legacyIncluded.filter(f => positionKeySet.has(f)),
        excluded: legacyExcluded.filter(f => positionKeySet.has(f))
      },
      employee: {
        included: legacyIncluded.filter(f => employeeKeySet.has(f)),
        excluded: legacyExcluded.filter(f => employeeKeySet.has(f))
      }
    };
  }
  return {
    position: { included: [], excluded: [] },
    employee: { included: legacyIncluded, excluded: legacyExcluded }
  };
}

export const readStoredExcelExportSettings = (
  report: ReportPartial
): {
  chainOfCommand: boolean;
  systemCalculations: boolean;
  accessibilityReport: boolean;
  includeFunctionalStatements: boolean;
  scope: string;
  categoryFields: CategoryFields;
  allowedExcelFields: string[];
} => {
  const exportSettings = localStorage.getItem("exportSettings");
  const storedSettings: MappedSettings = JSON.parse(exportSettings ?? "{}");
  const partitionId = getPartitionId(report);

  const allowedExcelFields = (() => {
    const doc: Nullish<QXDoc> = GetDoc();
    if (isNullish(doc)) {
      return [];
    }

    const excelKeys = doc.GetExcelKeys();
    if (!isNullish(excelKeys)) {
      return excelKeys;
    }

    if (doc.IsPositionDocument()) {
      const { keys, employeeKeys } = getEmployeePositionFields(doc);
      const allKeys = keys.concat(employeeKeys);
      return Array.from(new Set(allKeys)); // Remove duplicates
    }

    return doc
      .GetFieldList2(true, false, false, false)
      .map((item: any) => item.GetFieldName() ?? "");
  })();

  const getDefaultFields = () => {
    if (isInReporting()) {
      return report.columnNames;
    }

    return allowedExcelFields;
  };

  const excelFields = readExportSetting(
    storedSettings,
    partitionId,
    "excel",
    "fields",
    getDefaultFields
  );

  const excludedFields = readExportSetting(
    storedSettings,
    partitionId,
    "excel",
    "excludedFields",
    []
  );

  const defaultCategoryFields: CategoryFields = {
    position: { included: [], excluded: [] },
    employee: { included: [], excluded: [] }
  };

  let categoryFields = readExportSetting(
    storedSettings,
    partitionId,
    "excel",
    "categoryFields",
    defaultCategoryFields
  ) as CategoryFields;

  const storedExcel = storedSettings[partitionId ?? ""]?.["excel"] as
    Record<string, unknown> | undefined;
  const hasStoredCategoryFields = !isNullish(storedExcel?.["categoryFields"]);
  const hasStoredFields = !isNullish(storedExcel?.["fields"]);
  const hasStoredExcludedFields = !isNullish(storedExcel?.["excludedFields"]);

  if (allowedExcelFields.length > 0) {
    const allowedFieldSet = new Set<string>(allowedExcelFields as string[]);
    categoryFields = sanitizeCategoryFields(categoryFields, allowedFieldSet);

    if (!hasStoredCategoryFields) {
      const legacyIncluded = (excelFields as string[]).filter(f => allowedFieldSet.has(f));
      const legacyExcluded = (excludedFields as string[]).filter(f => allowedFieldSet.has(f));

      if (
        (hasStoredFields || hasStoredExcludedFields) &&
        (legacyIncluded.length > 0 || legacyExcluded.length > 0)
      ) {
        categoryFields = migrateLegacyFieldSelection(legacyIncluded, legacyExcluded, GetDoc());
      }
    }

    storeExportSettings("excel", "categoryFields", categoryFields, report);
  }

  return {
    chainOfCommand: readExportSetting(
      storedSettings,
      partitionId,
      "excel",
      "chainOfCommand",
      false
    ),
    systemCalculations: readExportSetting(
      storedSettings,
      partitionId,
      "excel",
      "systemCalculations",
      false
    ),
    includeFunctionalStatements: readExportSetting(
      storedSettings,
      partitionId,
      "excel",
      "includeFunctionalStatements",
      false
    ),
    accessibilityReport: readExportSetting(
      storedSettings,
      partitionId,
      "excel",
      "accessibilityReport",
      false
    ),
    scope: readExportSetting(storedSettings, partitionId, "excel", "scope", "currentbranch"),
    categoryFields,
    allowedExcelFields
  };
};

export const blsRestrictedScopes = [
  "entirechart",
  "toporphans",
  "orphans",
  "chainofcommand",
  "entirechartplus",
  "entireorg"
];

export type ScopeOption = {
  value: string;
  labelKey?: string;
  divider?: boolean;
  isBaseChartOnly?: boolean;
  isPlanAllowed?: boolean;
};

export const EXCEL_SCOPE_OPTIONS: ScopeOption[] = [
  { value: "entirechart", isPlanAllowed: true },
  { value: "currentbranch", isPlanAllowed: true },
  { divider: true, value: "currentpage", isPlanAllowed: true },
  { isBaseChartOnly: true, labelKey: "entirechartplus", value: "entireorg" },
  { isBaseChartOnly: true, value: "toporphans" },
  { isBaseChartOnly: true, labelKey: "orphanrecords", value: "orphans" },
  { isBaseChartOnly: false, value: "orphanrecords" },
  { isBaseChartOnly: false, value: "toporphans" },
  { isBaseChartOnly: false, value: "entirechartplus" },
  { isBaseChartOnly: false, value: "redeployed" }
];

export const filterExcelScopeOptions = (
  doc: Nullish<QXDoc>,
  hasActiveBLS: boolean
): ScopeOption[] => {
  const isPlanChart = doc?.IsPlanChart?.() === true;
  const isBaseChart = doc?.IsServerChart?.() === true && !isPlanChart;

  return EXCEL_SCOPE_OPTIONS.filter(option => {
    if (hasActiveBLS && blsRestrictedScopes.includes(option.value)) {
      return false;
    }
    if (isPlanChart && !option.isPlanAllowed) {
      return false;
    }
    if (!isNullish(option.isBaseChartOnly) && option.isBaseChartOnly !== isBaseChart) {
      return false;
    }
    return true;
  });
};

export const getAvailableExcelScopeValues = (
  doc: Nullish<QXDoc>,
  hasActiveBLS: boolean
): string[] => filterExcelScopeOptions(doc, hasActiveBLS).map(option => option.value);

export const getEmployeePositionFields = (doc: QXDoc) => {
  const objDb = doc.GetObjDB();
  const positionClass = objDb?.GetClassByName(enumObjClassNames.position);
  let keys = doc
    .GetFieldList2(true, false, false, false, false, true, positionClass)
    .map((item: any) => item.GetFieldName() ?? "");
  let employeeKeys = doc
    .GetFieldList2(true, false, false, false)
    .map((item: any) => item.GetFieldName() ?? "");

  return { keys, employeeKeys };
};
